/**
 * 来場希望日文字列（例: "7月9日（午前）"）を
 * 比較用の締切日時（Date）に変換する
 *
 * 例:
 * - 午前 -> 11:59:59
 * - 午後 -> 17:59:59
 *
 * @param {string} text - 例: "7月9日（午前）"
 * @param {string|number} year - 例: "2026"
 * @returns {Date|null}
 */
export function convertPlannedSlotToDeadline(text, year) {
  if (!text || !year) return null;

  // 余計な余白を消す
  const normalized = String(text).trim();

  // 例: 7月9日（午前）
  // 正規表現で分解
  const match = normalized.match(/(\d+)月(\d+)日（(午前|午後)）/);
  if (!match) return null;

  // 分解した内容をmonth、day、slotに格納
  const month = String(match[1]).padStart(2, "0");
  const day = String(match[2]).padStart(2, "0");
  const slot = match[3];

  const time = slot === "午前" ? "11:59:59" : "17:59:59";

  // JST前提
  return new Date(`${year}-${month}-${day}T${time}+09:00`);
}

/**
 * 来場希望日文字列をカンマ区切りで分割し、
 * 指定日(date)に該当する希望枠だけ取り出す
 *
 * 例:
 * "7月8日（午前）,7月9日（午後）"
 *   + targetDate = "2026-07-09"
 * -> ["7月9日（午後）"]
 *
 * @param {string} preferredVisitText
 * @param {string} targetDate - "YYYY-MM-DD"
 * @returns {string[]}
 */
export function extractPlannedSlotsForDate(preferredVisitText, targetDate) {
  if (!preferredVisitText || !targetDate) return [];

  // targetDateから年の情報を取り出す（来場希望日に情報がないため）
  const year = String(targetDate).slice(0, 4);

  return String(preferredVisitText)
    .split(",")             // カンマで分割
    .map((s) => s.trim())   // 空白除去
    .filter(Boolean)        // 無効な値は取り除いて、有効な値だけ抽出
    .filter((slotText) => {
      // 変換できるものだけ変換する "7月9日（午前）-> 11:59:59"
      const deadline = convertPlannedSlotToDeadline(slotText, year);
      if (!deadline) return false;

      // JSTに変換
      const slotDate = formatDateJst(deadline);
      return slotDate === targetDate;
    });
}

/**
 * Date を JST の YYYY-MM-DD 文字列にする
 *
 * @param {Date|string} value
 * @returns {string}
 */
export function formatDateJst(value) {

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  // JSTに変換
  const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);

  const y = jst.getUTCFullYear();
  const m = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const d = String(jst.getUTCDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

/**
 * 「受付済み / 遅れ / 何もなし」を判定する
 *
 * 判定ルール:
 * 1. checkinAt があれば "checked"
 * 2. checkinAt が無い場合
 *    - 指定日(targetDate)の来場希望枠を取り出す
 *    - その枠の締切日時を1つでも過ぎていれば "late"
 *    - そうでなければ null
 *
 * @param {Object} params
 * @param {string} params.preferredVisitText - 例: "7月9日（午前）,7月10日（午後）"
 * @param {string} params.targetDate - 例: "2026-07-09"
 * @param {string|Date|null} params.checkinAt - 受付履歴の日時
 * @param {Date} [params.now=new Date()] - 比較用現在時刻
 * @returns {"checked" | "late" | null}
 */
export function getVisitStatus({
  preferredVisitText,
  targetDate,
  visited,
  now = new Date(),
}) {
  // 受付履歴があるなら最優先で受付済み
  if (visited) {
    return "checked";
  }

  if (!preferredVisitText || !targetDate) {
    return null;
  }

  // 当日来る予定がある場合はslotsにデータが入ってくる
  const year = String(targetDate).slice(0, 4);
  const slots = extractPlannedSlotsForDate(preferredVisitText, targetDate);

  if (slots.length === 0) {
    return null;
  }

  // 締め切り時間を作る 例：["午後"] → [2026-07-09 17:59:59]
  // その日の希望枠の締切を1つでも過ぎていれば遅れ
  const deadlines = slots
    .map((slotText) => convertPlannedSlotToDeadline(slotText, year))
    .filter(Boolean);

  if (deadlines.length === 0) {
    return null;
  }

  // 遅れ判定 その日のサイトの締切を超えたらlate
  const nowTime = now.getTime();
  // 1つでも締切を過ぎていたら遅れにする場合はコメントを外す
  // const isLate = deadlines.some((d) => nowTime > d.getTime());
  const latestDeadline = Math.max(...deadlines.map(d => d.getTime()));
  const isLate = nowTime > latestDeadline;

  return isLate ? "late" : "ready";
}